import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Database, Users, ArrowRight, Network, AlertCircle, ClipboardList,
  ChevronRight, ChevronLeft, Compass, UserPlus, Building2, ShieldCheck, GraduationCap, Workflow, Layers,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Top-level categories: Onboarding Team  |  System Flows            */
/* ------------------------------------------------------------------ */
const topCategories = [
  {
    num: 1, key: 'onboarding_team_2', type: 'group',
    icon: GraduationCap, color: 'bg-amber-500', ring: 'ring-amber-200', accent: 'text-amber-600',
    title: 'Onboarding Team',
    desc: 'Training flows for the onboarding team — User Management, Org Structure and Report Issue.',
    tags: ['User Management', 'Org Structure', 'Report Issue'],
  },
  {
    num: 2, key: 'user_mgmt_arch', type: 'journey',
    icon: Layers, color: 'bg-teal-600', ring: 'ring-teal-200', accent: 'text-teal-600',
    title: 'User Management Architecture',
    desc: 'Interactive walkthrough of the Eclaims architecture — three pillars, structure, categories, role catalog, assignment model and standards alignment.',
    tags: ['8 scenes', 'ISO 27001', 'NIST', 'HIPAA'],
  },
  {
    num: 3, key: 'system_flows', type: 'group',
    icon: Workflow, color: 'bg-indigo-600', ring: 'ring-indigo-200', accent: 'text-indigo-600',
    title: 'System Flows',
    desc: 'End-to-end product walkthroughs — Acella data sync, issue reporting and survey lifecycle.',
    tags: ['Acella', 'Report Issue', 'Survey'],
  },
];

/* ------------------------------------------------------------------ */
/*  Onboarding Team sub-categories                                    */
/* ------------------------------------------------------------------ */
const onboardingSubCategories = [
  {
    key: 'user_management', type: 'group',
    icon: Users, color: 'bg-teal-600', ring: 'ring-teal-200', accent: 'text-teal-600',
    title: 'User Management',
    desc: '3 focused flows — adding users, linking facilities and assigning roles per facility.',
    tags: ['Add User', 'Facility', 'Roles'],
  },
  {
    key: 'community', type: 'journey',
    icon: Network, color: 'bg-amber-500', ring: 'ring-amber-200', accent: 'text-amber-600',
    title: 'Community Structure',
    desc: 'Understand how Shafafiya organises facilities — standalone or grouped, facility categories, user assignment.',
    tags: ['Org Hierarchy', 'Standalone', 'Categories'],
  },
  {
    key: 'report', type: 'journey',
    icon: AlertCircle, color: 'bg-red-500', ring: 'ring-red-200', accent: 'text-red-500',
    title: 'Report Issue',
    desc: 'How users file complaints from the portal — select module, attach files and submit. Shown for training context.',
    tags: ['Form', 'Module', 'Submit'],
  },
];

/* ------------------------------------------------------------------ */
/*  System Flows sub-categories                                       */
/* ------------------------------------------------------------------ */
const systemFlowsSubCategories = [
  {
    key: 'acella', type: 'journey',
    icon: Database, color: 'bg-teal-600', ring: 'ring-teal-200', accent: 'text-teal-600',
    title: 'Acella Onboarding',
    desc: 'How organisation and facility data flows from Acella — profiles built, invites sent, users access portal.',
    tags: ['Acella Sync', 'Profiles', 'Invites', 'OTP', 'Portal'],
  },
  {
    key: 'report', type: 'journey',
    icon: AlertCircle, color: 'bg-red-500', ring: 'ring-red-200', accent: 'text-red-500',
    title: 'Report Issue',
    desc: 'Users report portal issues through a complaint form — select module, attach files, submit.',
    tags: ['Form', 'Module', 'Submit'],
  },
  {
    key: 'survey', type: 'journey',
    icon: ClipboardList, color: 'bg-indigo-500', ring: 'ring-indigo-200', accent: 'text-indigo-500',
    title: 'Survey Creation',
    desc: '3-persona workflow — Creator builds, Reviewer checks, Publisher approves and publishes.',
    tags: ['Build', 'Configure', 'Review', 'Approve'],
  },
];

/* ------------------------------------------------------------------ */
/*  User Management mini-flows                                        */
/* ------------------------------------------------------------------ */
const userManagementFlows = [
  {
    key: 'um_add', type: 'journey',
    icon: UserPlus, color: 'bg-teal-600', ring: 'ring-teal-200', accent: 'text-teal-600',
    title: 'How to add a user',
    desc: 'End-to-end flow: open the Users page, fill basic details, assign roles, review, confirm invite, success.',
    tags: ['6 steps', 'Full wizard'],
  },
  {
    key: 'um_facility', type: 'journey',
    icon: Building2, color: 'bg-amber-500', ring: 'ring-amber-200', accent: 'text-amber-600',
    title: 'How to assign facility (multi-facility)',
    desc: 'Focused on the Organisation + Linked Facility fields. Shows how one user can belong to multiple facilities.',
    tags: ['2 steps', 'Multi-facility'],
  },
  {
    key: 'um_roles', type: 'journey',
    icon: ShieldCheck, color: 'bg-indigo-500', ring: 'ring-indigo-200', accent: 'text-indigo-500',
    title: 'How to assign roles',
    desc: 'Focused on the roles step — different role per facility (Admin in one, User in another).',
    tags: ['3 steps', 'Per-facility roles'],
  },
];

/* ------------------------------------------------------------------ */
/*  Onboarding Team 2 sub-categories (new design)                      */
/* ------------------------------------------------------------------ */
const onboardingTeam2SubCategories = [
  {
    key: 'user_management_2', type: 'group',
    icon: Users, color: 'bg-teal-600', ring: 'ring-teal-200', accent: 'text-teal-600',
    title: 'User Management',
    desc: '3 focused flows in the new design — adding users, linking facilities and assigning roles per facility.',
    tags: ['Add User', 'Facility', 'Roles'],
  },
  {
    key: 'community_2', type: 'journey',
    icon: Network, color: 'bg-amber-500', ring: 'ring-amber-200', accent: 'text-amber-600',
    title: 'Org Structure',
    desc: 'New design — community hierarchy, facility categories, facilities list and facility detail page.',
    tags: ['Org Hierarchy', 'Facilities', 'Facility Detail'],
  },
  {
    key: 'report_2', type: 'journey',
    icon: AlertCircle, color: 'bg-red-500', ring: 'ring-red-200', accent: 'text-red-500',
    title: 'Report Issue',
    desc: 'New design — complaint form with module dropdown, file upload and submitted confirmation.',
    tags: ['Form', 'Module', 'Submit'],
  },
];

const userManagement2Flows = [
  {
    key: 'um2_add', type: 'journey',
    icon: UserPlus, color: 'bg-teal-600', ring: 'ring-teal-200', accent: 'text-teal-600',
    title: 'How to add a user',
    desc: 'New design — open Users, fill basic details, assign roles, review, invite, success.',
    tags: ['6 steps', 'Full wizard'],
  },
  {
    key: 'um2_facility', type: 'journey',
    icon: Building2, color: 'bg-amber-500', ring: 'ring-amber-200', accent: 'text-amber-600',
    title: 'How to assign facility (multi-facility)',
    desc: 'New design — Organisation + Linked Facility fields. One user, multiple facilities.',
    tags: ['2 steps', 'Multi-facility'],
  },
  {
    key: 'um2_roles', type: 'journey',
    icon: ShieldCheck, color: 'bg-indigo-500', ring: 'ring-indigo-200', accent: 'text-indigo-500',
    title: 'How to assign roles',
    desc: 'New design — different role per facility (Admin in one, User in another).',
    tags: ['3 steps', 'Per-facility roles'],
  },
];

/* ------------------------------------------------------------------ */
/*  Card                                                              */
/* ------------------------------------------------------------------ */
function Card({ step, onClick, showStep }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ x: 6, boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)' }}
      onClick={onClick}
      data-testid={`journey-${step.key}`}
      className="bg-white rounded-2xl border border-black/5 shadow-lg p-5 cursor-pointer transition-all group flex items-center gap-5"
    >
      <div className="flex-shrink-0 flex flex-col items-center gap-1">
        <div className={`w-12 h-12 rounded-2xl ${step.color} ring-4 ${step.ring} flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <step.icon size={22} className="text-white" />
        </div>
        {showStep && <span className="font-heading font-extrabold text-xs text-[#737373]">STEP {step.num}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-1">{step.title}</h3>
        <p className="font-body text-sm text-[#737373] leading-relaxed mb-2.5">{step.desc}</p>
        <div className="flex flex-wrap gap-1.5">
          {step.tags.map(tag => (
            <span key={tag} className="px-2 py-0.5 rounded-md bg-[#F5F5F5] text-[10px] font-body font-medium text-[#737373]">{tag}</span>
          ))}
        </div>
      </div>
      <div className={`flex-shrink-0 ${step.accent} opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all`}>
        <ArrowRight size={20} />
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Header                                                            */
/* ------------------------------------------------------------------ */
function SelectorHeader({ crumb, onBack, subtitle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center mb-8 max-w-2xl"
    >
      <div className="inline-flex items-center gap-2 px-4 py-2 mb-5 rounded-full bg-teal-600/10 border border-teal-600/20">
        <Compass size={16} className="text-teal-600" />
        <span className="text-sm font-body font-medium text-teal-700 tracking-wide uppercase">Guide</span>
      </div>
      <h1 className="font-heading font-extrabold text-5xl md:text-6xl tracking-tight text-[#0A0A0A] leading-[0.95]">
        Shafafiya
      </h1>
      {crumb && (
        <div className="mt-5 flex items-center justify-center gap-2 text-sm font-body text-[#737373]">
          <button onClick={onBack} data-testid="selector-back" className="flex items-center gap-1 hover:text-teal-700 transition-colors">
            <ChevronLeft size={14} /> Back
          </button>
          {crumb.map((c, i) => (
            <span key={i} className="flex items-center gap-2">
              <span>/</span><span className={i === crumb.length - 1 ? 'text-[#0A0A0A] font-medium' : ''}>{c}</span>
            </span>
          ))}
        </div>
      )}
      {!crumb && (
        <p className="mt-4 font-body text-lg text-[#737373] leading-relaxed">
          Pick a section to explore the Shafafiya platform
        </p>
      )}
      {subtitle && (
        <p className="mt-3 font-body text-sm text-[#737373]">{subtitle}</p>
      )}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */
export default function JourneySelector({ onSelect, initialStack = [] }) {
  const [stack, setStack] = useState(initialStack);
  const goTo = (level) => setStack([...stack, level]);
  const goBack = () => setStack(stack.slice(0, -1));
  const currentLevel = stack[stack.length - 1] || null;

  let cards, crumb = null, subtitle = null;
  if (currentLevel === null) {
    cards = topCategories;
  } else if (currentLevel === 'onboarding_team') {
    cards = onboardingSubCategories;
    crumb = ['Onboarding Team'];
    subtitle = 'Training modules for the onboarding team';
  } else if (currentLevel === 'system_flows') {
    cards = systemFlowsSubCategories;
    crumb = ['System Flows'];
    subtitle = 'End-to-end product walkthroughs';
  } else if (currentLevel === 'user_management') {
    cards = userManagementFlows;
    crumb = ['Onboarding Team', 'User Management'];
    subtitle = 'Choose a flow to demo';
  } else if (currentLevel === 'onboarding_team_2') {
    cards = onboardingTeam2SubCategories;
    crumb = ['Onboarding Team'];
    subtitle = 'Training modules for the onboarding team';
  } else if (currentLevel === 'user_management_2') {
    cards = userManagement2Flows;
    crumb = ['Onboarding Team', 'User Management'];
    subtitle = 'Choose a flow to demo';
  }

  const handleCardClick = (card) => {
    if (card.type === 'group') {
      goTo(card.key);
    } else {
      onSelect(card.key, stack);
    }
  };

  return (
    <div data-testid="journey-selector" className="min-h-screen px-6 py-10 flex flex-col items-center">
      <SelectorHeader crumb={crumb} onBack={goBack} subtitle={subtitle} />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentLevel ?? 'root'}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-3xl space-y-3"
        >
          {cards.map((c, i) => (
            <div key={c.key}>
              <Card step={c} onClick={() => handleCardClick(c)} showStep={false} />
              {i < cards.length - 1 && (
                <div className="h-2" />
              )}
            </div>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
